import React, { useContext, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import Header from './Header';
import Home from './Home';
import { BodyContainer } from './ICardio.style';
import Login from './Login';
import { UserContext } from '../services/contexts';
import { IUser } from '../models/auth';
import useHandleSearchRedirect from 'hooks/handleSearchRedirect';
import Viewer from './Studies/Viewer';
import AnnotationSets from './Studies/AnnotationSets';
import { useGetDicomQuery, useGetDicomsQuery } from '../redux/slices/icardio';
import { ReviewModeProvider } from 'services/providers';

const IcidRouter = () => {
  const { icid } = useParams<{ icid?: string }>();

  if (!icid) return null;

  const getDicomsParams = {
    [icid.startsWith('st-') ? 'studyIcid' : icid.startsWith('fr-') ? 'frameIcid' : '']: icid,
    paginate: false,
  };

  // study ICID → fetch dicoms
  const { data: dicoms } = useGetDicomsQuery(getDicomsParams, {
    skip: !icid || !(icid.startsWith('st') || icid.startsWith('fr-')),
  });

  if (icid.startsWith('st-')) {
    if (!dicoms || !Array.isArray(dicoms)) return <div>Loading study…</div>;
    if (dicoms.length === 0) return <div>No dicoms found</div>;

    const firstDicomIcid = dicoms[0].icid;
    return <Navigate to={`/${icid}/${firstDicomIcid}`} replace />;
  }

  if (icid.startsWith('fr-')) {
    if (!dicoms || !Array.isArray(dicoms)) return <div>Loading dicom…</div>;
    if (dicoms.length === 0) return <div>No dicom found</div>;
    if (dicoms.length > 1) return <div>Multiple dicoms found for frame ICID {icid}</div>;

    const firstDicomIcid = dicoms[0].icid;
    return <Navigate to={`/${dicoms[0].studyIcid}/${firstDicomIcid}/${icid}`} replace />;
  }

  // dicom ICID → fetch dicom
  const { data: dicom } = useGetDicomQuery(icid as string, {
    skip: !icid || !icid.startsWith('di-'),
  });

  if (icid.startsWith('di-')) {
    if (!dicom) return <div>Loading dicom…</div>;
    return <Navigate to={`/${dicom.studyIcid}/${icid}`} replace />;
  }

  if (icid.startsWith('fr-')) {
    if (!dicom) return <div>Loading dicom…</div>;
    return <Navigate to={`/${dicom.studyIcid}/${dicom.icid}/${icid}`} replace />;
  }

  if (icid.startsWith('an-') || icid.startsWith('fu-') || icid.startsWith('as-')) {
    return <AnnotationSets />;
  }

  return <div>Unknown ICID type: {icid}</div>;
};

const ICardio = () => {
  const { user, setUser } = useContext(UserContext);
  const userIsLoggedIn = !!user;

  const rawUser = localStorage.getItem('user');
  const navigate = useNavigate();

  useEffect(() => {
    if (rawUser == undefined) {
      navigate('/login');
    } else {
      const cUser = JSON.parse(rawUser) as IUser;
      setUser(cUser);
    }
  }, [rawUser]);

  useHandleSearchRedirect();

  return (
    <ReviewModeProvider>
      <BodyContainer>
        {userIsLoggedIn && <Header />}
        <Routes>
          <Route path="/login" Component={Login} />
          {userIsLoggedIn && (
            <>
              <Route path="/" Component={Home} />
              <Route path="/:icid" Component={IcidRouter} />
              <Route path="/:studyIcid/:dicomIcid/:frameIcid?/:frameIndex?" Component={Viewer} />
            </>
          )}
          <Route path="*" element={<Navigate to={'/login'} />} />
        </Routes>
      </BodyContainer>
    </ReviewModeProvider>
  );
};

export default ICardio;
